import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronDown, Download, Flag, Info, Search, ShieldCheck, Undo2, X } from "lucide-react";
import Layout from "@/components/Layout";
import StickyFilters from "@/components/StickyFilters";
import ViewToggle, { useViewMode } from "@/components/ViewToggle";
import DateRangeFilter, { EMPTY_RANGE, rangeParams } from "@/components/DateRangeFilter";
import { Chips, IssuePills, Pill, SocietyDots, writerLine } from "@/components/Music";
import { TrackDrawer, artistLine } from "@/components/Tracks";
import { EmptyState, LoadingPanel } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/Calendar";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { api, apiError, downloadFile, formatDate } from "@/lib/api";
import { useRemembered } from "@/lib/useRemembered";
import { cn } from "@/lib/utils";

// where a song stands at one society
const REG = [
  ["pending", "Pending"],
  ["submitted", "Submitted"],
  ["registered", "Registered"],
  ["rejected", "Rejected"],
  ["na", "Doesn't apply"],
];
const REG_LABEL = Object.fromEntries(REG);
const DOT = {
  pending: "bg-muted2/60",
  submitted: "bg-sky-600 dark:bg-sky-400",
  registered: "bg-stock-in",
  rejected: "bg-stock-out",
  na: "border-[1.5px] border-dashed border-muted2 bg-transparent",
};

// "update for me" switch, remembered on this device
function useSwitch(name, start) {
  const key = `mt_reg_${name}`;
  const [on, setOn] = useState(() => {
    try {
      const v = window.localStorage.getItem(key);
      return v === null ? start : v === "1";
    } catch {
      return start;
    }
  });
  const set = (v) => {
    setOn(v);
    try {
      window.localStorage.setItem(key, v ? "1" : "0");
    } catch {
      /* private window: just for this visit */
    }
  };
  return [on, set];
}

/** A small text box saved when you leave it or press Enter. */
function NoteBox({ value, onSave, placeholder, label }) {
  const [v, setV] = useState(value || "");
  useEffect(() => setV(value || ""), [value]);
  return (
    <input
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => v.trim() !== (value || "") && onSave(v.trim())}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      maxLength={500}
      placeholder={placeholder}
      aria-label={label}
      className={cn(
        "mt-1.5 block w-full max-w-[250px] h-8 px-2.5 rounded-md border bg-card text-[12px] outline-none focus:border-brand-ring",
        v.trim() ? "border-line" : "border-orange-400/70 dark:border-orange-400/60"
      )}
    />
  );
}

/** The song's status at the society you're on, plus the whole-song "Action needed" flag. */
function SongStatusCell({ t, society, label, canEdit, onStatus, onAction }) {
  const r = t.regs[society] || { status: "pending" };
  const action = t.reg_status === "Action needed";
  if (!canEdit) {
    return (
      <span className="inline-flex items-center gap-2 h-8 text-[13px] whitespace-nowrap">
        <span className={cn("w-2 h-2 rounded-full shrink-0", DOT[r.status])} />
        {REG_LABEL[r.status]}
      </span>
    );
  }
  return (
    <div>
      <div className="flex items-center gap-1.5">
        <label className="relative inline-flex items-center h-8 rounded-lg border border-line bg-card pl-2.5 hover:border-brand-ring focus-within:border-brand-ring transition-colors">
          <span className={cn("w-2 h-2 rounded-full shrink-0", DOT[r.status])} />
          <select
            value={r.status}
            onChange={(e) => onStatus([t.track_id], e.target.value)}
            aria-label={`${label} status for ${t.title}`}
            className="appearance-none bg-transparent h-full pl-2 pr-7 text-[13px] outline-none cursor-pointer"
          >
            {REG.map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
          <ChevronDown className="w-3.5 h-3.5 absolute right-2 text-muted2 pointer-events-none" />
        </label>
        <button
          type="button"
          onClick={() => onAction([t.track_id], !action, "")}
          aria-pressed={action}
          title={action ? "Action needed from the writer (whole song). Click to clear." : "Ask the writer for something (whole song, every society)"}
          className={cn(
            "inline-flex items-center justify-center h-8 rounded-lg border text-[12px] font-semibold transition-colors",
            action ? "px-2.5 gap-1 border-transparent bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300" : "w-8 border-dashed border-line text-muted2 hover:text-ink hover:border-brand-ring"
          )}
        >
          <Flag className="w-3.5 h-3.5" />
          {action && <span className="max-xl:hidden">Action needed</span>}
        </button>
      </div>
      {r.status === "rejected" && (
        <NoteBox value={r.note} placeholder={`Why ${label} rejected it…`} label={`Why ${label} rejected ${t.title}`} onSave={(note) => onStatus([t.track_id], "rejected", { note })} />
      )}
      {action && <NoteBox value={t.reg_status_note} placeholder="What you need from the writer…" label={`What you need from the writer for ${t.title}`} onSave={(note) => onAction([t.track_id], true, note)} />}
    </div>
  );
}

const DATE_FIELDS = [
  { value: "release_date", label: "Release date" },
  { value: "created_at", label: "Date added" },
];

export default function Registrations() {
  const { user } = useAuth();
  const canEdit = user?.role !== "writer";
  const { settings, societyLabel } = useSettings();
  const [params, setParams] = useSearchParams();
  const society = params.get("society") || "BMI";
  const [show, setShow] = useState("open"); // open | ready | problems | done | na
  const [mode, setMode] = useViewMode("registrations", "list");
  const [range, setRange] = useState(EMPTY_RANGE);
  const [q, setQ] = useState("");
  const [summary, setSummary] = useRemembered("registrations-summary");
  const [tracks, setTracks] = useRemembered("registrations-tracks");
  const [selected, setSelected] = useState(new Set());
  const [open, setOpen] = useState(null);
  // Click anywhere on a track to open it — except on its own controls (checkbox, links, buttons)
  // or when you're selecting text to copy (an ISRC, say).
  const openRow = (e, t) => {
    if (e.target.closest("input, a, button, label, [data-no-open]")) return;
    if (window.getSelection()?.toString()) return;
    setOpen(t.track_id);
  };
  const [markDate, setMarkDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [autoSubmitted, setAutoSubmitted] = useSwitch("auto_submitted", true);
  const [resetSongs, setResetSongs] = useState([]);
  const [showReset, setShowReset] = useState(false);
  const req = useRef(0);

  const load = useCallback(async () => {
    const id = ++req.current;
    try {
      const [sm, tr] = await Promise.all([api.get("/registrations/summary"), api.get("/tracks", { params: { q, sort: "title", ...rangeParams(range) } })]);
      if (id !== req.current) return;
      setSummary(sm.data);
      setTracks(tr.data.tracks, !q && !range.from && !range.to);
      if (canEdit) api.get("/registrations/reset-note").then(({ data }) => id === req.current && setResetSongs(data.songs || [])).catch(() => {});
    } catch (err) {
      toast.error(apiError(err));
    }
  }, [q, range, setSummary, setTracks, canEdit]);

  useEffect(() => {
    const t = setTimeout(load, q ? 200 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  useEffect(() => setSelected(new Set()), [society, show, range, q]);

  const groups = useMemo(() => {
    const all = tracks || [];
    const st = (t) => t.regs[society]?.status || "pending";
    const openRows = all.filter((t) => !["registered", "na"].includes(st(t)));
    return {
      open: openRows,
      ready: openRows.filter((t) => !t.blocking.length),
      problems: openRows.filter((t) => t.blocking.length),
      done: all.filter((t) => st(t) === "registered"),
      na: all.filter((t) => st(t) === "na"),
    };
  }, [tracks, society]);
  const rows = groups[show];
  const label = societyLabel(society);

  const toggle = (t) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(t.track_id) ? n.delete(t.track_id) : n.add(t.track_id);
      return n;
    });
  const selectable = show === "done" || show === "na" ? rows : rows.filter((t) => !t.blocking.length);
  const allSelected = selectable.length > 0 && selectable.every((t) => selected.has(t.track_id));

  // a society status — shown right away, saved in the background
  const setSoc = async (ids, status, { quiet = false, keepRegistered = false, note } = {}) => {
    setTracks((all) =>
      (all || []).map((t) => {
        const cur = t.regs[society] || { status: "pending" };
        if (!ids.includes(t.track_id) || (keepRegistered && cur.status === "registered")) return t;
        if (cur.status === status && note === undefined) return t;
        const next = { status, done: status === "registered", date: status === "registered" ? (cur.status === "registered" ? cur.date : markDate) : "", note: status === "rejected" ? (note ?? (cur.status === "rejected" ? cur.note : "")) : "" };
        return { ...t, regs: { ...t.regs, [society]: next } };
      })
    );
    try {
      const { data } = await api.post("/registrations/status", { society, track_ids: ids, status, date: markDate, keep_registered: keepRegistered, ...(note !== undefined ? { note } : {}) });
      if (!quiet) toast.success(note !== undefined ? `Reason saved` : ids.length === 1 ? `${REG_LABEL[status]} at ${label}` : `${data.changed} song${data.changed === 1 ? "" : "s"} set to ${REG_LABEL[status]} at ${label}`);
      api.get("/registrations/summary").then(({ data: sm }) => setSummary(sm)).catch(() => {});
      return data.changed;
    } catch (err) {
      toast.error(apiError(err));
      load();
    }
  };

  // the whole-song "Action needed" flag
  const setAction = async (ids, on, note) => {
    setTracks((all) => (all || []).map((t) => (ids.includes(t.track_id) ? { ...t, reg_status: on ? "Action needed" : "", reg_status_note: on ? note || "" : "" } : t)));
    try {
      await api.post("/registrations/action", { track_ids: ids, on, note: note || "" });
      toast.success(on ? (note ? "Note for the writer saved" : "Action needed: add what you need from the writer") : "Action needed cleared");
    } catch (err) {
      toast.error(apiError(err));
      load();
    }
  };

  const mark = async (done) => {
    try {
      const ids = [...selected];
      const { data } = await api.post("/registrations/mark", { society, track_ids: ids, done, date: markDate });
      toast.success(done ? `${data.changed} marked as registered at ${label}` : `${data.changed} marked as not registered`);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const exportBatch = () => {
    const ids = selected.size ? [...selected] : groups.ready.map((t) => t.track_id);
    if (!ids.length) return toast.error("Nothing ready to file yet");
    downloadFile("/registrations/export.csv", `${society}-batch.csv`, { society, ids: ids.join(",") })
      .then(async () => {
        if (autoSubmitted) await setSoc(ids, "submitted", { quiet: true, keepRegistered: true });
        toast.success(`Filing sheet for ${ids.length} work${ids.length === 1 ? "" : "s"} downloaded${autoSubmitted ? ` · marked Submitted at ${label}` : ""}`);
        setSelected(new Set());
      })
      .catch((e) => toast.error(apiError(e)));
  };

  const setSociety = (k) => setParams({ society: k });
  const current = open ? (tracks || []).find((t) => t.track_id === open) : null;

  return (
    <Layout title="Registrations" subtitle="What still needs filing at each society">
      {!summary ? (
        <LoadingPanel rows={8} />
      ) : (
        <>
          {resetSongs.length > 0 && (
            <div className="rounded-2xl border border-gold/30 bg-gold-light px-4 py-3 mb-3 flex items-start gap-3 text-[13px]">
              <Info className="w-4 h-4 text-gold mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <b>
                  {resetSongs.length} song{resetSongs.length === 1 ? "" : "s"} went back to Pending.
                </b>{" "}
                Statuses are now per society, and {resetSongs.length === 1 ? "this one wasn't" : "these weren't"} tied to one. Set them again on the right society when you're ready.
                <div className="mt-2">
                  <button type="button" onClick={() => setShowReset((v) => !v)} className="text-[12.5px] font-semibold text-brand">
                    {showReset ? "Hide" : "Show"} the song{resetSongs.length === 1 ? "" : "s"}
                  </button>
                </div>
                {showReset && (
                  <ul className="mt-2 space-y-1">
                    {resetSongs.map((x) => (
                      <li key={x.track_id}>
                        <button type="button" onClick={() => setOpen(x.track_id)} className="font-semibold hover:text-brand">
                          {x.title}
                        </button>
                        <span className="text-muted2">
                          {x.artist ? ` · ${x.artist}` : ""} · was {x.was}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                type="button"
                aria-label="Dismiss"
                className="p-1 text-muted2 hover:text-ink"
                onClick={() => {
                  setResetSongs([]);
                  api.delete("/registrations/reset-note").catch(() => {});
                }}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 mb-2">
            {summary.map((s) => (
              <button
                key={s.key}
                onClick={() => setSociety(s.key)}
                aria-pressed={s.key === society}
                className={cn(
                  "rounded-2xl border p-4 text-left transition-colors",
                  s.key === society ? "bg-brand border-brand text-white" : "bg-card border-line hover:border-brand-ring"
                )}
              >
                <div className="font-mono text-[12px] font-semibold tracking-[0.08em]">{s.label}</div>
                <div className="font-display text-[28px] font-semibold mt-1.5 leading-none">{s.outstanding}</div>
                <div className={cn("text-[11.5px] mt-1.5", s.key === society ? "text-white/80" : "text-muted2")}>
                  to register · {s.ready} ready
                </div>
                <div className={cn("h-1 rounded-full mt-3", s.key === society ? "bg-white/25" : "bg-surface")}>
                  <div className={cn("h-1 rounded-full", s.key === society ? "bg-white" : "bg-sec-mech")} style={{ width: `${s.total ? (s.registered / s.total) * 100 : 0}%` }} />
                </div>
              </button>
            ))}
          </div>

          <StickyFilters className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Chips
                value={show}
                onChange={setShow}
                items={[
                  { key: "open", label: "Not registered", count: groups.open.length },
                  { key: "ready", label: "Ready to file", count: groups.ready.length },
                  { key: "problems", label: "Needs fixing first", count: groups.problems.length },
                  { key: "done", label: "Registered", count: groups.done.length },
                  ...(groups.na.length ? [{ key: "na", label: "Doesn't apply", count: groups.na.length }] : []),
                ]}
              />
              <div className="flex-1" />
              <label className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" aria-label="Search" className="h-9 w-[200px] pl-9 pr-3 rounded-lg border border-line bg-card text-[13px] outline-none focus:border-brand-ring" />
              </label>
              <DateRangeFilter value={range} onChange={setRange} fields={DATE_FIELDS} />
              <ViewToggle mode={mode} onChange={setMode} />
            </div>
          </StickyFilters>

          <section className="panel overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 flex-wrap border-b border-line">
              <div className="font-display text-[18px] font-semibold">
                {label} — {show === "done" ? "registered" : show === "na" ? "doesn't apply" : show === "ready" ? "ready to file" : show === "problems" ? "fix these first" : "not yet registered"}
              </div>
              {selected.size > 0 && <Pill tone="brand">{selected.size} selected</Pill>}
              <div className="flex-1" />
              {canEdit && (
                <label className={cn("relative inline-flex items-center h-9 rounded-lg border border-line bg-card", selected.size ? "hover:border-brand-ring" : "opacity-50")}>
                  <select
                    value=""
                    disabled={!selected.size}
                    onChange={(e) => {
                      if (!e.target.value) return;
                      setSoc([...selected], e.target.value);
                      setSelected(new Set());
                    }}
                    aria-label={`Set the ${label} status on the ticked songs`}
                    className="appearance-none bg-transparent h-full pl-3 pr-8 text-[13px] font-semibold outline-none cursor-pointer disabled:cursor-default"
                  >
                    <option value="">{label} status…</option>
                    {REG.map(([k, l]) => (
                      <option key={k} value={k}>
                        {l}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="w-3.5 h-3.5 absolute right-2.5 text-muted2 pointer-events-none" />
                </label>
              )}
              {canEdit && show !== "done" && show !== "na" && (
                <>
                  <div className="flex items-center gap-2 text-[12.5px] text-muted2" title="The date the songs were registered">
                    <span className="max-2xl:hidden">Registered on</span>
                    <DatePicker className="h-9 w-[150px]" clearable={false} value={markDate} onChange={setMarkDate} ariaLabel="Registered on" />
                  </div>
                  <Button variant="outline" disabled={!selected.size} onClick={() => mark(true)}>
                    <Check className="w-4 h-4 mr-2" /> Mark as registered
                  </Button>
                </>
              )}
              {canEdit && show === "done" && (
                <Button variant="outline" disabled={!selected.size} onClick={() => mark(false)}>
                  <Undo2 className="w-4 h-4 mr-2" /> Mark as not registered
                </Button>
              )}
              {show !== "done" && show !== "na" && (
                <Button className="bg-brand hover:bg-brand-hover" onClick={exportBatch}>
                  <Download className="w-4 h-4 mr-2" /> Export {label} batch
                  {selected.size ? ` (${selected.size})` : groups.ready.length ? ` (${groups.ready.length} ready)` : ""}
                </Button>
              )}
            </div>
            {canEdit && show !== "done" && show !== "na" && (
              <div className="flex items-center gap-x-6 gap-y-2 px-5 py-2.5 border-b border-line bg-surface/40 flex-wrap text-[12.5px] text-muted2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={autoSubmitted} onChange={(e) => setAutoSubmitted(e.target.checked)} className="w-4 h-4 accent-[hsl(var(--brand))]" />
                  When I export, mark them <b className="text-ink font-semibold">Submitted</b> at {label}
                </label>
                <span className="inline-flex items-center gap-1.5">
                  <Flag className="w-3.5 h-3.5" /> asks the writer for something (the whole song)
                </span>
              </div>
            )}

            {rows.length === 0 ? (
              <EmptyState icon={ShieldCheck} title={show === "done" ? `Nothing registered at ${label} yet` : show === "na" ? `Every song goes to ${label}` : `All caught up at ${label}`} body={show === "problems" ? "No tracks are blocked." : undefined} />
            ) : mode === "grid" ? (
              <div className="p-4 grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {rows.map((t) => {
                  const blocked = show !== "done" && show !== "na" && t.blocking.length > 0;
                  return (
                    <div
                      key={t.track_id}
                      onClick={(e) => openRow(e, t)}
                      className={cn("rounded-xl border p-4 space-y-3 bg-card cursor-pointer hover:border-brand-ring transition-colors", selected.has(t.track_id) ? "border-brand ring-2 ring-brand-ring" : "border-line")}
                    >
                      <div className="flex items-start gap-3">
                        {canEdit && (
                          <input
                            type="checkbox"
                            disabled={blocked}
                            aria-label={`Select ${t.title}`}
                            checked={selected.has(t.track_id)}
                            onChange={() => toggle(t)}
                            className="w-4 h-4 mt-1 accent-[hsl(var(--brand))] disabled:opacity-30"
                          />
                        )}
                        <button onClick={() => setOpen(t.track_id)} className="text-left min-w-0 flex-1">
                          <div className="font-semibold text-[14px] truncate">{t.title}</div>
                          <div className="text-[12px] text-muted2 truncate">{artistLine(t)}</div>
                        </button>
                        <SocietyDots regs={t.regs} size="sm" labels={societyLabel} />
                      </div>
                      <div className="font-mono text-[11.5px] text-muted2">{t.isrc || "No ISRC"}</div>
                      <div className="text-[12px] text-muted2 truncate">{writerLine(t.writers)}</div>
                      <div>{show === "done" ? <Pill tone="good">Registered {t.regs[society].date ? formatDate(t.regs[society].date) : ""}</Pill> : <IssuePills issues={t.blocking.length ? t.blocking : t.issues.filter((i) => i.code === "writer_ipi")} max={2} />}</div>
                      <div className="pt-2 border-t border-line" data-no-open>
                        <SongStatusCell t={t} society={society} label={label} canEdit={canEdit} onStatus={setSoc} onAction={setAction} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px]">
                  <thead>
                    <tr className="bg-surface/70 border-b border-line">
                      <th className="w-10 px-4 py-2.5 text-left">
                        {canEdit && (
                          <input
                            type="checkbox"
                            aria-label="Select all"
                            checked={allSelected}
                            onChange={() => setSelected(allSelected ? new Set() : new Set(selectable.map((t) => t.track_id)))}
                            className="w-4 h-4 accent-[hsl(var(--brand))]"
                          />
                        )}
                      </th>
                      {["Track", "ISRC", "Writers", "B M H R", show === "done" ? `${label} registered` : "Before you file", `${label} status`].map((h) => (
                        <th key={h} className="t-head text-left px-3 py-2.5 font-medium">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((t) => {
                      const blocked = show !== "done" && show !== "na" && t.blocking.length > 0;
                      return (
                        <tr key={t.track_id} onClick={(e) => openRow(e, t)} className={cn("t-row cursor-pointer", selected.has(t.track_id) && "bg-brand-light/50")}>
                          <td className="px-4" data-no-open>
                            {canEdit && (
                              <input
                                type="checkbox"
                                disabled={blocked}
                                title={blocked ? "Fix the problems first" : undefined}
                                aria-label={`Select ${t.title}`}
                                checked={selected.has(t.track_id)}
                                onChange={() => toggle(t)}
                                className="w-4 h-4 accent-[hsl(var(--brand))] disabled:opacity-30"
                              />
                            )}
                          </td>
                          <td className="t-cell max-w-[190px] 2xl:max-w-[260px]">
                            <button onClick={() => setOpen(t.track_id)} className="text-left block max-w-full">
                              <div className="font-semibold truncate hover:text-brand">{t.title}</div>
                              <div className="text-[12px] text-muted2 truncate">
                                {t.artist}
                                {t.release_date ? ` · ${formatDate(t.release_date)}` : ""}
                              </div>
                            </button>
                          </td>
                          <td className="t-cell font-mono text-[12px] whitespace-nowrap">{t.isrc || <span className="text-stock-out">Missing</span>}</td>
                          <td className="t-cell text-[12.5px] text-muted2 max-w-[150px] 2xl:max-w-[240px] truncate">{writerLine(t.writers)}</td>
                          <td className="t-cell">
                            <SocietyDots regs={t.regs} labels={societyLabel} />
                          </td>
                          <td className="t-cell">
                            {show === "done" ? (
                              <span className="text-[12.5px]">{t.regs[society].date ? formatDate(t.regs[society].date) : "Registered"}</span>
                            ) : show === "na" ? (
                              <span className="text-[12.5px] text-muted2">Doesn't go to {label}</span>
                            ) : blocked ? (
                              <div className="flex items-center gap-x-2 gap-y-1 flex-wrap max-w-[130px]">
                                <IssuePills issues={t.blocking} max={1} />
                                {canEdit && (
                                  <Link to={`/catalog/${t.track_id}`} className="text-[12px] font-semibold text-brand">
                                    Fix
                                  </Link>
                                )}
                              </div>
                            ) : (
                              <Pill tone="good">Ready</Pill>
                            )}
                          </td>
                          <td className="t-cell" data-no-open>
                            <SongStatusCell t={t} society={society} label={label} canEdit={canEdit} onStatus={setSoc} onAction={setAction} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          <p className="text-[12.5px] text-muted2 mt-3">
            Tracks with a missing ISRC, no writers or splits that don't add to 100% can't be selected until they're fixed. The export is a CSV with one row per work and
            each writer's PRO, IPI and share, plus {settings.publisher_name || "your publisher"} as publisher.
          </p>
        </>
      )}
      <TrackDrawer track={current} open={!!current} onOpenChange={(v) => !v && setOpen(null)} canEdit={canEdit} />
    </Layout>
  );
}
